/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { FeProvisioningService } from "@tivocorp/data-domain-lib";

import type { IParentalSettingsInteractor } from "../../interfaces";
import type { TNetworkOptions, TParentalSettingsUpdate } from "../../types";
import { buildFeProvisioningServiceError } from "../../error";
import { Log } from "../../logging";
import { ApplicationFeatureArea } from "../../enums";
import { ParentalRuleEngine } from "../../parentalControl";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor, ServiceLocator } from "../base";

/**
 * Facade for persisted Parental Control settings writes. Resolves the
 * FeProvisioning gateway endpoint via TSRI, builds authenticated params and
 * runs the call through the NetworkGuard layer.
 */
export class ParentalSettingsInteractor
  extends BaseInteractor
  implements IParentalSettingsInteractor
{
  private static readonly TAG: string = "ParentalSettingsInteractor";

  public async updateParentalSettings(
    update: TParentalSettingsUpdate,
    options?: TNetworkOptions
  ): Promise<void> {
    const request = ParentalRuleEngine.getInstance().buildSettingsWrite(update);
    return this.executeGuarded(
      "updateParentalSettings",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(
          ParentalSettingsInteractor.TAG,
          "updateParentalSettings() called"
        );
        const service: FeProvisioningService = ServiceLocator.getInstance(
          FeProvisioningService,
          await this.buildServicesParams(
            TIVO_SERVICES.FeatureProvisioningGateway,
            ApplicationFeatureArea.SETTINGS
          )
        );
        try {
          await service.updateAccountFeatures(request, guardedSignal);
        } catch (error: unknown) {
          // The builder re-attaches the response, so executeGuarded still
          // sees a 401 and replays the call with a fresh token.
          throw buildFeProvisioningServiceError(error);
        }
      },
      options
    );
  }
}
